import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {HTTPError} from 'ky';
import {useCallback} from 'react';

import {InspectionSubType, InspectionType, Model, ModelCounts} from '~redux/types/models';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {useCurrentModel} from 'src/contexts/ModelContext';
import {
  getAdvancedConfiguration,
  getCombinedPreview,
  getDefectBookStats,
  getModelInferenceCriteria,
  getModelsCounts,
  getModelSelectionMetric,
  getModelsV2,
  getModelTrainingConfig,
  getPreprocessingRois,
} from './models';

export const ModelsQueryKeys = {
  all: ['models'],
  allModels: (projectId: string) => [...ModelsQueryKeys.all, 'allModels', projectId] as const,
  modelCounts: (projectId: string) => [...ModelsQueryKeys.all, 'modelCounts', projectId] as const,
  modelTrainingConfiguration: (projectId: string, modelId?: string) =>
    [...ModelsQueryKeys.all, 'modelTrainingConfiguration', projectId, modelId] as const,
  modelSelectionMetric: (projectId: string, modelId: string) => [
    ...ModelsQueryKeys.all,
    'modelSelectionMetric',
    projectId,
    modelId,
  ],
  currentModelAdvancedConfiguration: (projectId: string, modelId?: string) => [
    ...ModelsQueryKeys.all,
    'currentModelAdvancedConfiguration',
    projectId,
    modelId,
  ],
  combinedPreview: (projectId: string, modelId?: string) => [
    ...ModelsQueryKeys.all,
    'combinedPreview',
    projectId,
    modelId,
  ],
  preprocessingRois: (projectId: string, modelId?: string) => [
    ...ModelsQueryKeys.all,
    'preprocessingRois',
    projectId,
    modelId,
  ],
  modelInferenceCriteria: (projectId: string, modelId?: string) => [
    ...ModelsQueryKeys.all,
    'modelInferenceCriteria',
    projectId,
    modelId,
  ],
  defectBookStats: (projectId: string, modelId?: string) => [
    ...ModelsQueryKeys.all,
    'defectBookStats',
    projectId,
    modelId,
  ],
} as const;

const transformModel = (model: Model): Model => ({
  ...model,
  // ensure that every label item has an alias by falling back to the label.id
  labels: model.labels.map((label) => ({...label, alias: label.alias || label.id})),
  // Quick hack for now: Treat rule_based models as feature classification models. Can be removed once rule
  // based are implemented properly
  inspectionType: model.inspectionType === 'rule_based' ? InspectionType.classification : model.inspectionType,
  inspectionSubtype: model.inspectionType === 'rule_based' ? InspectionSubType.feature : model.inspectionSubtype,
});

export const mergeCounts = (model: Model, counts: ModelCounts | undefined): Model => {
  if (!counts) {
    return model;
  }
  return {
    ...model,
    imageCount: counts.imageCount,
    humanAnnotationCount: counts.humanAnnotationCount,
    defectAnnotationCount: counts.defectAnnotationCount,
    labelConditionCounts: counts.labelConditionCounts,
  };
};

const selectBaseModels = (data: Model[]): Model[] => data.map(transformModel).sort((a, b) => b.createdAt - a.createdAt);

const selectCountsByModelId = (counts: ModelCounts[]): Record<string, ModelCounts> =>
  counts.reduce<Record<string, ModelCounts>>((acc, entry) => {
    acc[entry.modelId] = entry;
    return acc;
  }, {});

/**
 * Per-model counts keyed by `modelId`. Driven by `/models/<project>/counts` so the heavy aggregation
 * is decoupled from the main model list (see `useModelsQuery`). Mutations that change models or
 * annotations should invalidate this key alongside `allModels`.
 */
export function useModelsCountsQuery(enabled: boolean = true): UseQueryResult<Record<string, ModelCounts>> {
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryKey = projectId ? ModelsQueryKeys.modelCounts(projectId) : (['models', 'modelCounts', ''] as const);

  return useQuery({
    queryKey,
    queryFn: ({signal}) => (projectId ? getModelsCounts(projectId, signal) : Promise.resolve([] as ModelCounts[])),
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60,
    select: selectCountsByModelId,
    enabled: enabled && !!projectId,
  });
}

/**
 * V2 model list without counts. Returns a referentially stable `Model[]` that only changes when the
 * model configuration itself changes — never with annotation activity. Use this wherever a changed
 * data reference has side effects (e.g. the annotator rebuilds its working state from it), and
 * `useModelsQuery` wherever count fields are displayed.
 */
export function useBaseModelsQuery(enabled: boolean = true): UseQueryResult<Model[]> {
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryKey = projectId ? ModelsQueryKeys.allModels(projectId) : (['models', 'allModels', ''] as const);

  return useQuery({
    queryKey,
    queryFn: ({signal}) => (projectId ? getModelsV2(projectId, signal) : Promise.resolve([] as Model[])),
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 60, // 1 hour
    select: selectBaseModels,
    enabled: enabled && !!projectId,
  });
}

/**
 * Merges the lightweight V2 model list with the counts query so consumers keep the original
 * `Model[]` shape. Counts arrive in parallel and fill in once their request resolves. The merge
 * happens inside the TanStack `select` so the hook returns the underlying `UseQueryResult` directly
 * — preserving the stable-result-reference optimisation that lets downstream components bail out of
 * re-rendering when nothing has changed.
 */
export function useModelsQuery(enabled: boolean = true): UseQueryResult<Model[]> {
  const projectId = useAppSelector(selectCurrentProjectId);
  // Use a fallback value for the query key when projectId is empty to ensure consistent query keys
  const queryKey = projectId ? ModelsQueryKeys.allModels(projectId) : (['models', 'allModels', ''] as const);
  const counts = useModelsCountsQuery(enabled).data;

  const select = useCallback(
    (data: Model[]): Model[] => {
      const transformed = selectBaseModels(data);
      return counts ? transformed.map((m) => mergeCounts(m, counts[m.id])) : transformed;
    },
    [counts],
  );

  return useQuery({
    queryKey,
    queryFn: ({signal}) => (projectId ? getModelsV2(projectId, signal) : Promise.resolve([] as Model[])),
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 60, // 1 hour
    select,
    enabled: enabled && !!projectId,
  });
}

export function useSingleModelQuery(modelId: string): UseQueryResult<Model | null> {
  const projectId = useAppSelector(selectCurrentProjectId);
  const counts = useModelsCountsQuery().data;

  const select = useCallback(
    (models: Model[]): Model | null => {
      const match = models.find((model) => model.id === modelId);
      if (!match) return null;
      // Apply the same `transformModel` normalisation as `useModelsQuery` so consumers see a
      // consistent shape (rule_based → classification, label aliases filled in, etc.).
      const transformed = transformModel(match);
      return mergeCounts(transformed, counts?.[transformed.id]);
    },
    [modelId, counts],
  );

  return useQuery({
    queryKey: ModelsQueryKeys.allModels(projectId),
    queryFn: ({signal}) => getModelsV2(projectId, signal),
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 60, // 1 hour
    select,
  });
}

export function useCurrentModelHasSamplesQuery(): UseQueryResult<boolean> {
  const {currentModel} = useCurrentModel();
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryKey = projectId ? ModelsQueryKeys.modelCounts(projectId) : (['models', 'modelCounts', ''] as const);

  return useQuery({
    queryKey,
    queryFn: ({signal}) => (projectId ? getModelsCounts(projectId, signal) : Promise.resolve([] as ModelCounts[])),
    refetchOnWindowFocus: true,
    staleTime: 1000 * 10,
    select: (counts) => {
      const entry = counts.find((c) => c.modelId === currentModel?.id);
      return entry ? entry.imageCount > 0 : false;
    },
    enabled: !!projectId,
  });
}

export function useCurrentModelTrainingConfigurationQuery(
  isAdmin: boolean,
  modelId?: string,
): UseQueryResult<ModelTrainingConfigurationResponse> {
  const {currentModel} = useCurrentModel();
  const projectId = useAppSelector(selectCurrentProjectId);
  return useQuery({
    queryKey: ModelsQueryKeys.modelTrainingConfiguration(projectId, modelId ?? currentModel?.id),
    queryFn: ({signal}) => getModelTrainingConfig(projectId, modelId ?? currentModel!.id, signal),
    enabled: (!!modelId || !!currentModel?.id) && isAdmin,
  });
}

/**
 * Hook to fetch the selection metric for a model
 */
export function useModelSelectionMetricQuery(): UseQueryResult<SelectionMetricResponse> {
  const {currentModel} = useCurrentModel();
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery({
    queryKey: ModelsQueryKeys.modelSelectionMetric(projectId, currentModel!.id),
    queryFn: ({signal}) => getModelSelectionMetric(projectId, currentModel!.id, signal),
    enabled: Boolean(currentModel?.id),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

export function useCurrentModelAdvancedConfigurationQuery(
  isAdmin: boolean,
  currentModelId?: string,
): UseQueryResult<ModelAdvancedConfigurationResponse> {
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();

  const modelId = currentModelId || currentModel?.id;
  return useQuery<ModelAdvancedConfigurationResponse, HTTPError>({
    queryKey: ModelsQueryKeys.currentModelAdvancedConfiguration(projectId, modelId),
    queryFn: () =>
      getAdvancedConfiguration({
        projectId,
        modelId: modelId!, // non-null as query is disabled when no modelId
      }),
    enabled: Boolean(modelId && isAdmin),
  });
}

export function useCombinedPreviewQuery(enabled: boolean = false) {
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();

  return useQuery<CombinedPreviewResponse, HTTPError>({
    queryKey: ModelsQueryKeys.combinedPreview(projectId, currentModel?.id),
    queryFn: ({signal}) => getCombinedPreview({projectId, modelId: currentModel!.id, reactQuerySignal: signal}),
    enabled: Boolean(currentModel) && enabled,
    staleTime: 1000 * 60,
  });
}

export function useModelInferenceCriteriaQuery() {
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();

  return useQuery<ModelInferenceCriteriaResponse, HTTPError>({
    queryKey: ModelsQueryKeys.modelInferenceCriteria(projectId, currentModel?.id),
    queryFn: ({signal}) => getModelInferenceCriteria(projectId, currentModel!.id, signal),
    enabled: Boolean(currentModel?.id),
  });
}

export function usePreprocessingRoisQuery(currentModelId?: string): UseQueryResult<ModelRoisResponse> {
  const {currentModel} = useCurrentModel();
  const projectId = useAppSelector(selectCurrentProjectId);

  const modelId = currentModelId || currentModel?.id;
  return useQuery({
    queryKey: ModelsQueryKeys.preprocessingRois(projectId, modelId),
    queryFn: ({signal}) => getPreprocessingRois(projectId, modelId!, signal),
    enabled: Boolean(modelId),
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to fetch defect book statistics for a model
 */
export function useDefectBookStatsQuery(modelId?: string): UseQueryResult<DefectBookStatsResponse> {
  const {currentModel} = useCurrentModel();
  const projectId = useAppSelector(selectCurrentProjectId);
  const targetModelId = modelId ?? currentModel?.id;

  return useQuery({
    queryKey: ModelsQueryKeys.defectBookStats(projectId, targetModelId),
    queryFn: ({signal}) => getDefectBookStats(projectId, targetModelId!, signal),
    refetchOnWindowFocus: true,
    enabled: Boolean(targetModelId),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
